import getVehicleRoutes from 'features/candidate/Candidates/Table/Detail/Operation/utils/getVehicleRoutes';
import useFeatureFlags from 'features/auth/utils/useFeatureFlags';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
  } from 'react';
import { GetVehicleResponseV2VM } from '@optimization/sa-common';
import { parseVehicleRoutes } from 'features/candidate/Candidates/Table/Detail/Operation/utils/useVehicleRoutes';
import { roundTo, useIds } from '@optimization/ssi-common';
import { setGlobalLoadingProgressPercent } from 'features/globalLoading/globalLoadingSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useParams } from 'react-router-dom';
import { VehicleRoutes } from 'features/candidate/Candidates/Table/Detail/Operation/utils/routingTypes';
import {
  useLazyGetChassisRouteQuery,
  useLazyGetVehicleQuery,
  useUpdateVehicleMutation,
  useGetVehiclesQuery,
} from 'app/services/solution';

const longestDrivenDayEnabled = false;

export type FleetRoutes = Record<string, VehicleRoutes>;

interface Fleet {
  isLoadingFleetRoutes: boolean;
  fleetRoutes: FleetRoutes;
  loadFleetRoutes: () => void;
}

const FleetContext = createContext<Fleet | null>(null);

export const FleetProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const [getVehicle] = useLazyGetVehicleQuery();
  const [getChassisRoute] = useLazyGetChassisRouteQuery();
  const [updateVehicle] = useUpdateVehicleMutation();

  const featureFlags = useFeatureFlags();
  const { solutionId } = useParams();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const vehiclesQuery = useGetVehiclesQuery(solutionId || '', { skip: !solutionId || !accessToken });
  const [fleetRoutes, setFleetRoutes] = useState<FleetRoutes>({});
  const [isLoadingFleetRoutes, setIsLoadingFleetRoutes] = useState(false);
  const [triedVehicleIds, setTriedVehicleIds] = useState<number[]>([]);

  const triedVehicleIdsRef = useRef<typeof triedVehicleIds | null>(null);
  triedVehicleIdsRef.current = triedVehicleIds;

  const vehicleIds = useIds(vehiclesQuery.data);

  const updateProgress = useCallback(
    (index: number, total: number) => {
      const progressPercent = roundTo(((index + 1) / total) * 100, 0);
      dispatch(setGlobalLoadingProgressPercent({ progressPercent }));
    },
    [dispatch],
  );

  const tryChassisRoute = useCallback(
    async (vehicle: GetVehicleResponseV2VM) => {
      const chassisNumber = vehicle.ScaniaChassis?.ChassisNumber;

      if (!chassisNumber || !solutionId) return;

      const response = await getChassisRoute({
        solutionId,
        vehicleId: vehicle.Id,
        longestDrivenDayEnabled,
      });

      if (!response.isSuccess) return;

      const routesPayload = getVehicleRoutes({
        // @ts-ignore
        routePointsResponse: response.data,
        longestDrivenDayEnabled,
      });

      await updateVehicle({
        solutionId,
        vehicle: {
          ...vehicle,
          Routes: JSON.stringify(routesPayload),
        },
      });

      setFleetRoutes((prev) => ({ ...prev, [vehicle.Id]: routesPayload }));
    },
    [getChassisRoute, updateVehicle, solutionId],
  );

  const fetchAndParseVehicle = useCallback(
    async ({ vehicleId, vehiclesCount, index }: { vehicleId: number; index: number; vehiclesCount: number }) => {
      if (solutionId) {
        const result = await getVehicle({ solutionId, vehicleId });

        setTriedVehicleIds((prev) => [...prev, vehicleId]);

        if (!result.isSuccess) return;

        const parsedRoutes = parseVehicleRoutes(result.data);

        if (parsedRoutes) {
          setFleetRoutes((prev) => ({ ...prev, [vehicleId]: parsedRoutes }));
        } else {
          await tryChassisRoute(result.data);
        }

        updateProgress(index, vehiclesCount);
      }
    },
    [solutionId, tryChassisRoute, getVehicle, updateProgress],
  );

  const loadFleetRoutes = useCallback(() => {
    if (
      !vehicleIds ||
      !vehicleIds.length ||
      !solutionId ||
      !featureFlags?.Geospatial ||
      isLoadingFleetRoutes ||
      vehicleIds.every((vehicleId) =>
        triedVehicleIdsRef.current ? triedVehicleIdsRef.current.includes(vehicleId) : true,
      )
    ) {
      return;
    }

    const load = async () => {
      setIsLoadingFleetRoutes(true);

      if (vehicleIds.length) {
        dispatch(setGlobalLoadingProgressPercent({ progressPercent: 0 }));

        for (let i = 0; i < vehicleIds.length; i++) {
          // This is done one vehicle at a time to avoid putting too much load on the API
          await fetchAndParseVehicle({
            vehicleId: vehicleIds[i],
            vehiclesCount: vehicleIds.length,
            index: i,
          });
        }

        dispatch(setGlobalLoadingProgressPercent({ progressPercent: undefined }));
      }

      setIsLoadingFleetRoutes(false);
    };

    load();
  }, [vehicleIds, solutionId, isLoadingFleetRoutes, featureFlags?.Geospatial, dispatch, fetchAndParseVehicle]);

  const value = useMemo(
    () => ({
      isLoadingFleetRoutes,
      fleetRoutes,
      loadFleetRoutes,
    }),
    [isLoadingFleetRoutes, fleetRoutes, loadFleetRoutes],
  );

  return <FleetContext.Provider value={value}>{children}</FleetContext.Provider>;
};

export const useFleet = () => useContext(FleetContext);
